import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { cloneDeep } from 'lodash'

import { TrashIcon } from '../../Icons/TrashIcon'
import { CurrencyInputField } from '../../CurrencyInputField'
import BillingPriceConfigTable from '../BillingPriceConfigTable'
import { CHANNEL_PRICE_BUSINESS_TYPES } from '../constants'
import { supportsBusinessType, isPriceEmpty, findDuplicateIndexes, findChannelsMissingBusinessTypePair, areChannelPricesValid } from '../utils'
import { channelDisplayName } from '../channelNames'

const ChannelPricesTable = ({
  channelPrices = [],
  availableChannels = [],
  currency,
  onUpdateChannelPrices,
  onIsValid = () => {},
  isLoadingChannels = false,
  hasChannelsError = false,
  requiresBusinessType = false,
}) => {
  const { i18n } = useTranslation()

  const headers = [
    { label: i18n.t('billing.tech_fee_services.channel_prices.headers.channel'), id: 'channel' },
    ...(requiresBusinessType ? [{ label: i18n.t('billing.tech_fee_services.channel_prices.headers.business_type'), id: 'business_type' }] : []),
    { label: i18n.t('billing.tech_fee_services.channel_prices.headers.price'), id: 'price' },
    { label: '', id: 'actions' },
  ]

  const businessTypeOptions = [
    { value: CHANNEL_PRICE_BUSINESS_TYPES.D2C, label: i18n.t('billing.tech_fee_services.channel_prices.business_types.d2c') },
    { value: CHANNEL_PRICE_BUSINESS_TYPES.B2B, label: i18n.t('billing.tech_fee_services.channel_prices.business_types.b2b') },
  ]

  const sortedChannels = [...availableChannels].sort((a, b) => {
    return channelDisplayName(a.value, i18n).toLowerCase().localeCompare(channelDisplayName(b.value, i18n).toLowerCase())
  })

  const isChannelInCatalog = channel => sortedChannels.some(({ value }) => value === channel)

  // The catalog is the same set the API accepts, so a channel outside it is rejected on save. Only
  // judged once the catalog actually loaded: otherwise a failed request would invalidate every row.
  const isCatalogUsable = !isLoadingChannels && !hasChannelsError && sortedChannels.length > 0
  const unavailableChannelCount = isCatalogUsable ? channelPrices.filter(({ channel }) => channel && !isChannelInCatalog(channel)).length : 0

  useEffect(() => {
    onIsValid(areChannelPricesValid(channelPrices, requiresBusinessType) && unavailableChannelCount === 0)
  }, [channelPrices, requiresBusinessType, unavailableChannelCount])

  const duplicateIndexes = findDuplicateIndexes(channelPrices)
  const channelsMissingPair = requiresBusinessType ? findChannelsMissingBusinessTypePair(channelPrices) : new Set()

  // A mixed store needs both business types priced, so selecting a channel seeds the missing row.
  const handleOnChangeChannel = (rowIndex, newChannel) => {
    const newChannelPrices = cloneDeep(channelPrices)
    const row = newChannelPrices[rowIndex]
    row.channel = newChannel

    if (!requiresBusinessType || !supportsBusinessType(newChannel)) {
      delete row.business_type
      onUpdateChannelPrices(newChannelPrices)
      return
    }

    const existingBusinessTypes = newChannelPrices
      .filter((channelPrice, index) => index !== rowIndex && channelPrice.channel === newChannel)
      .map(({ business_type }) => business_type)

    if (!row.business_type || existingBusinessTypes.includes(row.business_type)) {
      row.business_type = existingBusinessTypes.includes(CHANNEL_PRICE_BUSINESS_TYPES.D2C) ? CHANNEL_PRICE_BUSINESS_TYPES.B2B : CHANNEL_PRICE_BUSINESS_TYPES.D2C
    }

    const missing = [CHANNEL_PRICE_BUSINESS_TYPES.D2C, CHANNEL_PRICE_BUSINESS_TYPES.B2B].filter(
      businessType => businessType !== row.business_type && !existingBusinessTypes.includes(businessType),
    )

    missing.forEach((businessType, offset) => {
      newChannelPrices.splice(rowIndex + 1 + offset, 0, {
        channel: newChannel,
        subchannel: null,
        business_type: businessType,
        price: null,
      })
    })

    onUpdateChannelPrices(newChannelPrices)
  }

  const handleOnChangeBusinessType = (rowIndex, newBusinessType) => {
    const newChannelPrices = cloneDeep(channelPrices)
    newChannelPrices[rowIndex].business_type = newBusinessType
    onUpdateChannelPrices(newChannelPrices)
  }

  const handleOnChangePrice = (rowIndex, newPrice) => {
    const newChannelPrices = cloneDeep(channelPrices)
    newChannelPrices[rowIndex].price = newPrice === '' ? null : newPrice
    onUpdateChannelPrices(newChannelPrices)
  }

  const handleOnDeleteRow = rowIndex => {
    onUpdateChannelPrices(channelPrices.filter((_, index) => index !== rowIndex))
  }

  const handleOnAddRow = () => {
    onUpdateChannelPrices([...cloneDeep(channelPrices), { channel: '', subchannel: null, price: null }])
  }

  const rows = channelPrices.map((channelPrice, rowIndex) => {
    const isDuplicate = duplicateIndexes.has(rowIndex)
    const isMissingPair = channelsMissingPair.has(channelPrice.channel)
    const isPriceInvalid = isPriceEmpty(channelPrice.price)
    const allowsBusinessType = supportsBusinessType(channelPrice.channel)
    // Rendered as its own option regardless of the catalog state, so a saved channel never falls
    // back to the placeholder and reads as unset while its value is still there and still billed.
    const isChannelMissingFromOptions = Boolean(channelPrice.channel) && !isChannelInCatalog(channelPrice.channel)
    const isChannelUnavailable = isChannelMissingFromOptions && isCatalogUsable
    const isChannelInvalid = !channelPrice.channel || isDuplicate || isMissingPair || isChannelUnavailable
    const isBusinessTypeInvalid = requiresBusinessType && allowsBusinessType && !channelPrice.business_type

    const channelErrorMessage = () => {
      if (isDuplicate) {
        return i18n.t('billing.tech_fee_services.channel_prices.errors.duplicated_row')
      }
      if (!channelPrice.channel) {
        return i18n.t('billing.tech_fee_services.channel_prices.errors.channel_required')
      }
      if (isChannelUnavailable) {
        return i18n.t('billing.tech_fee_services.channel_prices.errors.channel_unavailable')
      }
      return i18n.t('billing.tech_fee_services.channel_prices.errors.business_type_pair_required')
    }

    const cells = [
      {
        cell: (
          <select
            value={channelPrice.channel || ''}
            onChange={event => handleOnChangeChannel(rowIndex, event.target.value)}
            className="border-none focus:ring-0 w-full bg-transparent"
          >
            <option value="" hidden={true}>
              {i18n.t('billing.tech_fee_services.channel_prices.select_channel')}
            </option>

            {isChannelMissingFromOptions && <option value={channelPrice.channel}>{channelDisplayName(channelPrice.channel, i18n)}</option>}

            {sortedChannels.map(channel => (
              <option value={channel.value} key={channel.value}>
                {channelDisplayName(channel.value, i18n)}
              </option>
            ))}
          </select>
        ),
        id: 'channel',
        isInvalid: isChannelInvalid,
        errorMessage: channelErrorMessage(),
      },
    ]

    if (requiresBusinessType) {
      cells.push({
        cell: !channelPrice.channel ? (
          <div className="px-2 py-2" />
        ) : allowsBusinessType ? (
          <select
            value={channelPrice.business_type || ''}
            onChange={event => handleOnChangeBusinessType(rowIndex, event.target.value)}
            className="border-none focus:ring-0 w-full bg-transparent"
          >
            <option value="" hidden={true}>
              {i18n.t('billing.tech_fee_services.channel_prices.select_business_type')}
            </option>

            {businessTypeOptions.map(option => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <div className="px-2 py-2 text-gray-500">{i18n.t('billing.tech_fee_services.channel_prices.business_types.not_applicable')}</div>
        ),
        id: 'business_type',
        isInvalid: isBusinessTypeInvalid,
        errorMessage: i18n.t('billing.tech_fee_services.channel_prices.errors.business_type_required'),
        isDisabled: Boolean(channelPrice.channel) && !allowsBusinessType,
      })
    }

    cells.push(
      {
        cell: (
          <CurrencyInputField
            value={channelPrice.price === null ? '' : channelPrice.price}
            onChange={value => handleOnChangePrice(rowIndex, value)}
            customIcon={<div className="font-bold">{currency}</div>}
            scale={8}
            precision={16}
            inputClassName={'pl-12 border-none bg-transparent shadow-none focus:ring-0'}
          />
        ),
        id: 'price',
        isInvalid: isPriceInvalid,
        errorMessage: i18n.t('billing.tech_fee_services.channel_prices.errors.price_required'),
        className: 'border-r-0',
      },
      {
        cell: (
          <button className="px-2 outline-none flex items-center justify-center w-full" onClick={() => handleOnDeleteRow(rowIndex)}>
            <TrashIcon className="h-4 w-4 text-gray-600" />
          </button>
        ),
        id: 'actions',
        className: 'border-l-0',
      },
    )

    return cells
  })

  return (
    <div className="mt-3">
      <div className="text-sm text-gray-500">{i18n.t('billing.tech_fee_services.channel_prices.title')}</div>
      <div className="text-xs text-gray-400 mb-2">
        {i18n.t('billing.tech_fee_services.channel_prices.description')}
        {requiresBusinessType && <> {i18n.t('billing.tech_fee_services.channel_prices.mixed_store_hint')}</>}
      </div>

      {channelPrices.length > 0 && <BillingPriceConfigTable headers={headers} rows={rows} className="w-full" />}

      {hasChannelsError ? (
        <div className="mt-2 text-sm text-red-500 italic">{i18n.t('billing.tech_fee_services.channel_prices.load_error')}</div>
      ) : (
        <button
          className="mt-2 text-sm text-blue-500 hover:text-blue-600 outline-none disabled:text-gray-400"
          onClick={handleOnAddRow}
          disabled={isLoadingChannels || availableChannels.length === 0}
        >
          {isLoadingChannels
            ? i18n.t('billing.tech_fee_services.channel_prices.loading_channels')
            : i18n.t('billing.tech_fee_services.channel_prices.add_channel')}
        </button>
      )}
    </div>
  )
}

export default ChannelPricesTable
